import { ReactElement, PureComponent, SyntheticEvent } from 'react';

import { injectIntl, type IntlShape } from 'react-intl-next';

import { CodeSegment } from '@atlassian/bitkit-code';

import {
  createSideBySideLinePermalink,
  createUnifiedLinePermalink,
} from '../../common/diff-permalink';
import {
  DiffInlineRenderProp,
  HideLines,
  Line,
  LineAnnotation as LineAnnotationType,
  OnAddComment,
} from '../../common/types';
import {
  ReplaceBidiCharactersDiff,
  bidiRegExp,
} from '../../common/util/bidi-character-replacements';

import messages from './i18n';
import { InlineContent } from './inline-content';
import LineAnnotation from './line-annotation';
import { LineNumberDetails, LineNumbers } from './line-numbers';
import withTap from './with-tap';

const Code = withTap(props => <pre {...props} />);

type InjectedProps = {
  intl: IntlShape;
};

export type CodeLineProps = HideLines &
  InjectedProps & {
    activePermalink?: string;
    line: Line;
    highlightedContent?: CodeSegment;
    filePath?: string;
    index: number;
    inlineContent?: DiffInlineRenderProp;
    onAddComment?: OnAddComment;
    showPermalinks: boolean;
    onPermalinkClick?: (permalink: string) => void;
    lineAnnotations?: LineAnnotationType[];
    isPrAnnotationsEnabled?: boolean;
    highlightLine?: boolean;
  };

export class BaseCodeLine extends PureComponent<CodeLineProps> {
  addCommentButton: HTMLButtonElement | undefined;

  renderInlineContent(isActive: boolean): ReactElement {
    const { inlineContent, line } = this.props;

    return (
      <InlineContent
        inlineContent={inlineContent}
        line={line}
        isActive={isActive}
      />
    );
  }

  onAddComment = (event: SyntheticEvent | MouseEvent): void => {
    event.preventDefault();
    const { line, onAddComment } = this.props;

    if (onAddComment && line.type !== 'empty') {
      const { oldLine: from, newLine: to } = line;
      onAddComment({ from, to });
    }
  };

  createPermalink = (): string => {
    const { line, filePath, hideNewLines, hideOldLines } = this.props;
    if (!filePath) {
      return '';
    }
    if (hideNewLines || hideOldLines) {
      return createSideBySideLinePermalink(line, filePath, hideNewLines);
    }
    return createUnifiedLinePermalink(line, filePath);
  };

  getAriaLabelForLineType = (type: string, intl: IntlShape): string => {
    const ariaLabelLineTypeMap: { add: string; del: string; normal: string } = {
      add: intl.formatMessage(messages.lineAdded),
      del: intl.formatMessage(messages.lineRemoved),
      normal: intl.formatMessage(messages.lineUnchanged),
    };

    // @ts-ignore TODO: fix noImplicitAny error here
    return ariaLabelLineTypeMap[type] || ' ';
  };

  getLineTypeSymbol = (type: string): string => {
    const lineTypeSymbolMap: { add: string; del: string; normal: string } = {
      add: '+',
      del: '-',
      normal: '',
    };

    // @ts-ignore TODO: fix noImplicitAny error here
    return lineTypeSymbolMap[type] || ' ';
  };

  getLineNumberDetails(): LineNumberDetails[] {
    const { intl, hideOldLines, hideNewLines, line } = this.props;

    if (hideOldLines && hideNewLines) {
      return [];
    }

    const { oldLine, newLine, type } = line;

    const oldLineDetails = {
      number: oldLine,
      label: intl.formatMessage(messages.fromLine, {
        lineNumber: oldLine,
      }),
      type,
    };
    const newLineDetails = {
      number: newLine,
      label: intl.formatMessage(messages.toLine, {
        lineNumber: newLine,
      }),
      type,
    };

    if (hideOldLines && !hideNewLines) {
      return [newLineDetails];
    } else if (!hideOldLines && hideNewLines) {
      return [oldLineDetails];
    }

    return [oldLineDetails, newLineDetails];
  }

  render(): ReactElement {
    const {
      activePermalink,
      line,
      highlightedContent,
      index,
      intl,
      inlineContent,
      onAddComment,
      showPermalinks,
      lineAnnotations,
      isPrAnnotationsEnabled,
      onPermalinkClick,
      highlightLine,
    } = this.props;

    const { content, type, conflictType, wordDiff } = line;
    const isEmptyLine = line.type === 'empty';
    const permalink = this.createPermalink();

    const isActivePermalink = showPermalinks && activePermalink === permalink;

    const lineNumbers: LineNumberDetails[] = this.getLineNumberDetails();

    const getLineContents = () => {
      const lineType = this.getLineTypeSymbol(type);
      const ariaLabelForLineType = `${this.getAriaLabelForLineType(
        type,
        intl
      )} `;

      // For now, skip syntax highlighting if we find a bidirectional character.
      // Ultimately we want to allow syntax highlighting, but this is a quick
      // fix for internal testing. COREX-5805
      if (highlightedContent && !content.slice(1).match(bidiRegExp)) {
        // This span includes __rbb_syntax_highlight to prevent Refined
        // Bitbucket from trying to re-highlight the code, leading to React
        // getting confused. We include syntax-highlighted as well to provide an
        // semi-official tag indicating that extensions shouldn't highlight this
        // line.
        return (
          <div>
            <span className="diff-line-type">{lineType}</span>
            <span
              className="code-diff syntax-highlighted __rbb_syntax_highlight"
              data-line-type={lineType}
              aria-label={ariaLabelForLineType + content.slice(1)}
            >
              {highlightedContent}
            </span>
          </div>
        );
      }
      // do not render word diff if line contains bidi characters
      if (wordDiff && !wordDiff.match(bidiRegExp)) {
        // Inner span allows CSS to select just the code without the diff
        // marker so that tab sizes can be made to work properly.
        return (
          <div>
            <span className="diff-line-type">{lineType}</span>
            <span
              className="code-diff"
              data-line-type={lineType}
              aria-label={ariaLabelForLineType + content.slice(1)}
            >
              <span dangerouslySetInnerHTML={{ __html: wordDiff.slice(1) }} />
            </span>
          </div>
        );
      }
      return (
        // Inner span allows CSS to select just the code without the diff
        // marker so that tab sizes can be made to work properly.
        <div>
          <span className="diff-line-type">{lineType}</span>
          <span
            className="code-diff"
            data-line-type={lineType}
            aria-label={ariaLabelForLineType + content.slice(1)}
          >
            <span>{ReplaceBidiCharactersDiff(content.slice(1))}</span>
          </span>
        </div>
      );
    };

    return (
      <>
        <div
          style={{ position: 'relative', top: '-100px' }}
          id={permalink || undefined}
          data-qa="code-line-permalink"
        />
        <div
          className={[
            'line-wrapper',
            'gutter-width-apply-padding-left',
            `type-${type}`,
            conflictType ? `has-conflict-${conflictType}` : '',
            isActivePermalink ? 'active' : '',
            highlightLine ? 'highlighted' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          key={`chunk-line-${index}`}
          data-qa="code-line"
        >
          <LineNumbers
            isActivePermalink={isActivePermalink}
            lineNumbers={lineNumbers}
            onPermalinkClick={onPermalinkClick}
            permalink={permalink}
            showPermalinks={showPermalinks}
          />
          {onAddComment && !isEmptyLine && (
            <button
              className="add-comment-button"
              onClick={this.onAddComment}
              onTouchEnd={this.onAddComment}
              aria-label={intl.formatMessage(messages.addComment)}
              tabIndex={0}
            />
          )}
          <Code
            className={`code-component${highlightLine ? ' highlighted' : ''}`}
            onTap={this.onAddComment}
          >
            {getLineContents()}
          </Code>
          {isPrAnnotationsEnabled &&
          lineAnnotations &&
          lineAnnotations.length &&
          (type === 'add' || type === 'normal') ? (
            <LineAnnotation lineAnnotations={lineAnnotations} />
          ) : null}
          {inlineContent && this.renderInlineContent(isActivePermalink)}
        </div>
      </>
    );
  }
}

export const CodeLine = injectIntl(BaseCodeLine);
